/**
 * Mobile Fuel hub — KPI tiles + entry-points into Log / Vehicles / Equipment.
 * Single-column, touch-first.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Fuel, Truck, Cog, FilePlus2, AlertTriangle, ChevronRight, TrendingUp, TrendingDown,
} from 'lucide-react';
import { apiGet } from '../../lib/api';
import { formatINR } from '../../lib/format';

export default function MobileFuelHome() {
  const [kpi, setKpi] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiGet('/fuel/dashboard')
      .then(setKpi)
      .catch(() => setKpi(null))
      .finally(() => setLoading(false));
  }, []);

  const tiles = [
    { to: '/m/fuel/log', icon: FilePlus2, title: 'Refuelling Log', desc: 'Add a fuel entry', accent: 'from-emerald-500 to-emerald-700' },
    { to: '/m/fuel/vehicles', icon: Truck, title: 'Vehicles & Equipment', desc: 'Lookup · capacities · drivers', accent: 'from-indigo-500 to-indigo-700' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 pb-6" data-testid="m-fuel-home">
      <div className="bg-[#3b4872] text-white px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Link to="/m" className="p-1.5 -ml-1.5"><ArrowLeft size={20} /></Link>
        <div>
          <div className="font-display font-bold flex items-center gap-1.5"><Fuel size={14} /> Fuel Management</div>
          <div className="text-[10px] text-slate-300">Field log &amp; consumption</div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {loading ? (
          <div className="text-center text-slate-500 text-sm py-6">Loading KPIs…</div>
        ) : kpi ? (
          <div className="grid grid-cols-2 gap-2" data-testid="m-fuel-kpis">
            <Kpi label="Month Cost" value={formatINR(kpi.this_month_cost || 0)} accent="indigo" />
            <Kpi label="Month Litres" value={`${kpi.this_month_litres || 0} L`} accent="emerald" />
            <KpiDelta label="vs Last Month" pct={kpi.cost_delta_pct || 0} />
            <Kpi label="Active Alerts" value={kpi.active_alerts || 0} accent="rose" />
          </div>
        ) : null}

        <div className="space-y-2">
          {tiles.map((t) => (
            <Link
              key={t.to}
              to={t.to}
              data-testid={`m-fuel-tile-${t.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              className="bg-white border border-slate-200 hover:border-[#3b4872]/60 rounded-xl p-4 flex items-center gap-4 active:scale-[0.98] transition-all shadow-sm"
            >
              <div className={`w-11 h-11 rounded-lg bg-gradient-to-br ${t.accent} text-white flex items-center justify-center shrink-0`}>
                <t.icon size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-sm text-slate-900 leading-tight">{t.title}</div>
                <div className="text-[11px] text-slate-600 mt-0.5">{t.desc}</div>
              </div>
              <ChevronRight size={16} className="text-slate-300" />
            </Link>
          ))}
        </div>

        {Array.isArray(kpi?.tanks) && kpi.tanks.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid="m-fuel-tanks">
            <div className="text-[10px] uppercase tracking-widest font-display text-slate-500 px-1 mb-2 flex items-center gap-1.5">
              <Cog size={11} /> Tank Levels
            </div>
            <div className="space-y-2">
              {kpi.tanks.slice(0, 5).map((t) => {
                const pct = t.capacity_l ? Math.round((t.current_level_l / t.capacity_l) * 100) : 0;
                const color = pct < 20 ? 'bg-rose-500' : pct < 40 ? 'bg-amber-500' : 'bg-emerald-500';
                return (
                  <div key={t.id} className="text-xs">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="truncate flex-1 font-display font-bold text-slate-700">{t.tank_id} · {t.project_name || '—'}</span>
                      <span className="text-slate-500">{Math.round(t.current_level_l)} / {t.capacity_l} L</span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full ${color}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {kpi?.night_alerts_week > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 flex items-center gap-2">
            <AlertTriangle size={14} />
            <span>{kpi.night_alerts_week} after-hours fuelling alerts this week</span>
          </div>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, accent = 'slate' }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3">
      <div className="text-[9px] uppercase tracking-widest text-slate-500 font-display">{label}</div>
      <div className={`text-lg font-bold mt-0.5 text-${accent}-700 truncate`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5 truncate">{sub}</div>}
    </div>
  );
}

function KpiDelta({ label, pct }) {
  const up = pct > 0;
  const Icon = up ? TrendingUp : TrendingDown;
  const color = up ? 'text-rose-700' : 'text-emerald-700';
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3">
      <div className="text-[9px] uppercase tracking-widest text-slate-500 font-display">{label}</div>
      <div className={`text-lg font-bold mt-0.5 ${color} flex items-center gap-1`}>
        <Icon size={14} /> {Math.abs(pct)}%
      </div>
    </div>
  );
}
